export const mockData = {
  features: [
    {
      icon: "brain",
      title: "Adaptive Intelligence",
      description: "AI that learns from every interaction and contextualizes insights dynamically."
    },
    {
      icon: "globe",
      title: "Context Awareness",
      description: "Surfaces what matters most based on user behavior, industry trends, and business state."
    },
    {
      icon: "rocket",
      title: "Engagement Engine",
      description: "Nudges, prompts, and personalized workflows that keep users in flow."
    },
    {
      icon: "message-circle",
      title: "Narrative Insights",
      description: "Turn data into stories. Automatically generate summaries that explain why — not just what."
    },
    {
      icon: "repeat",
      title: "PLG Feedback Loop",
      description: "Every click teaches the system. The more you use it, the smarter it becomes."
    }
  ],
  
  comparisonData: [
    {
      feature: "Data Presentation",
      legacy: "Static dashboards with fixed KPIs",
      pricestrike: "Adaptive interface that learns what you need"
    },
    {
      feature: "User Experience",
      legacy: "One-size-fits-all layout",
      pricestrike: "Personalized to role, context, and engagement"
    },
    {
      feature: "Insights",
      legacy: "Shows data, leaves interpretation to you",
      pricestrike: "Generates narrative explanations and recommendations"
    },
    {
      feature: "Learning Curve",
      legacy: "Steep — requires training and onboarding",
      pricestrike: "Intuitive — adapts to you in real time"
    },
    {
      feature: "Intelligence",
      legacy: "Rules based alerts and triggers",
      pricestrike: "AI powered adaptive intelligence layer"
    }
  ],
  
  timelineSteps: [
    {
      number: "01",
      title: "Observe",
      description: "The Hero Hub watches how you interact: what you click, what you skip, when you engage."
    },
    {
      number: "02",
      title: "Learn",
      description: "Machine learning models identify patterns in your behavior and preferences."
    },
    {
      number: "03",
      title: "Adapt",
      description: "Your interface evolves — prioritizing what matters, hiding what doesn't."
    },
    {
      number: "04",
      title: "Grow",
      description: "As your business changes, so does PriceStrike. Continuous learning means continuous relevance."
    }
  ],
  
  useCases: [
    {
      title: "DTC Brand on Shopify",
      context: "Direct-to-consumer pricing",
      description: "A growing skincare brand selling on Shopify was underpricing bestsellers and overpricing slow-movers. PriceStrike's AI identified 23 products with immediate margin opportunity.",
      impact: "+41% gross margin in 60 days",
      smbFocus: "Perfect for DTC brands with 50-500 SKUs",
      image: "https://images.unsplash.com/photo-1688561807440-8a57dfa77ee3?w=800"
    },
    {
      title: "Ace Hardware Franchise",
      context: "Competing locally",
      description: "Single-location hardware franchise was losing customers to Home Depot on key items. PriceStrike helped them match on traffic drivers while maintaining margins on specialty products.",
      impact: "18% revenue lift, margins preserved",
      smbFocus: "Ideal for independent retailers",
      image: "https://images.unsplash.com/photo-1519520104014-df63821cb6f9?w=800"
    },
    {
      title: "Plumbing Supply Distributor",
      context: "B2B account pricing",
      description: "Family-owned distributor with 180 contractor accounts spent 20+ hours weekly managing custom price lists. PriceStrike automated tiered pricing with volume-based rules.",
      impact: "80% less time on pricing admin",
      smbFocus: "Built for 50-500 B2B accounts",
      image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800"
    },
    {
      title: "Farm-to-Table Bistro",
      context: "Ingredient cost recovery",
      description: "12-table restaurant with volatile ingredient costs was absorbing price increases instead of passing them through. PriceStrike's menu analysis revealed $4,200/month in lost margin.",
      impact: "+29% food margin recovered",
      smbFocus: "Great for single-location restaurants",
      image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800"
    },
    {
      title: "NAPA Auto Parts Dealer",
      context: "Retail vs. shop pricing",
      description: "Independent auto parts store with 4,800 SKUs needed different pricing for walk-in DIYers vs. local repair shops. PriceStrike created smart dual-pricing with automatic detection.",
      impact: "35% faster inventory turn",
      smbFocus: "Handles 1,000-10,000 SKU catalogs",
      image: "https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=800"
    },
    {
      title: "Digital Marketing Agency",
      context: "Retainer optimization",
      description: "15-person agency was leaving money on the table with flat-rate retainers. PriceStrike benchmarked against market rates and restructured packages based on deliverable value.",
      impact: "34% higher average contract value",
      smbFocus: "Perfect for 5-30 person agencies",
      image: "https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800"
    },
    {
      title: "CrossFit Gym",
      context: "Class pack vs. membership",
      description: "CrossFit box was losing unlimited members to cheaper class packs. PriceStrike redesigned tier structure to incentivize commitment without cannibalizing revenue.",
      impact: "+52% in monthly recurring revenue",
      smbFocus: "Ideal for boutique fitness studios",
      image: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=800"
    },
    {
      title: "Regional Lumber Yard",
      context: "Contractor loyalty pricing",
      description: "Third-generation lumber yard with 140 contractor accounts had no systematic way to reward loyalty. PriceStrike built automated tier progression based on spend history.",
      impact: "27% growth in contractor accounts",
      smbFocus: "Built for B2B building supply",
      image: "https://images.unsplash.com/photo-1634672652995-ee7525bce595?w=800"
    },
    {
      title: "Pet Supplies Plus Store",
      context: "Category price optimization",
      description: "Pet store owner discovered through PriceStrike that premium dog food was underpriced while accessories were overpriced, driving customers to Amazon for high-margin items.",
      impact: "+23% basket size, 15% margin lift",
      smbFocus: "Great for specialty retailers",
      image: "https://images.unsplash.com/photo-1583337130417-3346a1be7dee?w=800"
    },
    {
      title: "Family Dental Practice",
      context: "Cash-pay competitiveness",
      description: "4-dentist practice was losing uninsured patients to corporate chains. PriceStrike helped create competitive cash-pay bundles while maintaining profitability on complex procedures.",
      impact: "44% more treatment acceptance",
      smbFocus: "Perfect for independent practices",
      image: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=800"
    }
  ],
  
  metrics: [
    {
      value: 35,
      suffix: "%",
      label: "Average Margin Lift",
      description: "Across all verticals"
    },
    {
      value: 5,
      suffix: "x",
      label: "Faster Time-to-Insight",
      description: "From days to hours"
    },
    {
      value: 80,
      suffix: "%",
      label: "User Engagement Rate",
      description: "Daily active usage"
    }
  ],
  
  testimonials: [
    {
      quote: "PriceStrike transformed how we think about pricing. We went from reactive spreadsheet updates to proactive margin optimization.",
      author: "Michael Torres",
      role: "Director of Pricing",
      company: "Regional Retail Chain"
    },
    {
      quote: "The elasticity insights were eye-opening. We discovered we were underpricing our premium products by 15% for years.",
      author: "Amanda Wright",
      role: "VP of Revenue Operations",
      company: "B2B Distribution Company"
    },
    {
      quote: "Finally, pricing decisions backed by data instead of gut feel. Our margins improved within the first quarter.",
      author: "David Okonkwo",
      role: "Chief Financial Officer",
      company: "Manufacturing Firm"
    }
  ],
  
  footerLinks: {
    product: [
      { label: "Features", href: "/product" },
      { label: "Integrations", href: "/product#integrations" },
      { label: "Pricing", href: "/pricing" },
      { label: "Industries", href: "/industries" }
    ],
    freeTools: [
      { label: "Free Pricing Check", href: "/pricing-check" },
      { label: "ROI Calculator", href: "/roi-calculator" },
      { label: "Price Simulator", href: "/simulator" }
    ],
    industries: [
      { label: "Retail", href: "/industries" },
      { label: "Healthcare", href: "/industries" },
      { label: "Tech & SaaS", href: "/industries" },
      { label: "Manufacturing", href: "/industries" }
    ],
    company: [
      { label: "About", href: "/about" },
      { label: "Partners", href: "/partners" },
      { label: "Insights", href: "/insights" },
      { label: "Contact", href: "/contact" },
      { label: "SMB Playbook", href: "/strategy" },
      { label: "TED Talk", href: "/ted-talk" },
      { label: "Shopify Talk", href: "/shopify-talk" },
      { label: "Share Links", href: "/share-links" }
    ]
  }
};